import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { useAuth, API } from "../App";
import { ArrowLeft, Users, Eye, Globe, Smartphone, Monitor, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, BarChart, Bar } from "recharts";

const RANGES = [
  { label: "7 jours", value: 7 },
  { label: "30 jours", value: 30 },
  { label: "90 jours", value: 90 },
];

const SOURCE_LABEL = {
  direct: "Direct",
  google: "Google",
  facebook: "Facebook",
  whatsapp: "WhatsApp",
  twitter: "X/Twitter",
  linkedin: "LinkedIn",
  instagram: "Instagram",
  bing: "Bing",
  internal: "Navigation interne",
};

function Kpi({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testId}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-md bg-emerald-50 flex items-center justify-center">
          <Icon className="w-4 h-4 text-emerald-700" />
        </div>
        <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      </div>
      <p className="text-3xl font-serif font-light text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function AdminVitrineAnalyticsPage() {
  const { token } = useAuth();
  const [range, setRange] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/public/analytics/vitrine?range=${range}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (alive) setData(res.data); })
      .catch((err) => { console.error(err); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range, token]);

  const totals = data?.totals || { pageviews: 0, unique_visitors: 0 };
  const daily = data?.daily || [];
  const pages = data?.top_pages || [];
  const sources = data?.sources || [];
  const devices = data?.devices || [];

  const avgDailyVisitors = daily.length
    ? Math.round(daily.reduce((s, d) => s + (d.unique_visitors || 0), 0) / daily.length)
    : 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="vitrine-analytics-page">
      <Helmet title="Analytics du site vitrine | Superadmin CMES" />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/superadmin" className="inline-flex items-center gap-2 text-sm text-emerald-700 hover:text-emerald-900 mb-3" data-testid="back-superadmin">
              <ArrowLeft className="w-4 h-4" /> Retour Superadmin
            </Link>
            <h1 className="text-3xl font-serif text-slate-900">Analytics du site vitrine</h1>
            <p className="text-sm text-slate-500 mt-1">Statistiques de visite anonymisées — RGPD friendly, zéro cookie.</p>
          </div>
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-1" data-testid="range-selector">
            {RANGES.map((r) => (
              <button key={r.value} onClick={() => setRange(r.value)}
                data-testid={`range-${r.value}`}
                className={`px-4 py-2 text-xs font-semibold rounded-md transition-colors ${range === r.value ? "bg-emerald-700 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-500" data-testid="loading">Chargement…</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Kpi icon={Users} label="Visiteurs uniques" value={totals.unique_visitors.toLocaleString("fr-FR")} sub={`sur ${range} jour(s)`} testId="kpi-visitors" />
              <Kpi icon={Eye} label="Pages vues" value={totals.pageviews.toLocaleString("fr-FR")} sub="toutes pages publiques" testId="kpi-pageviews" />
              <Kpi icon={TrendingUp} label="Moyenne / jour" value={avgDailyVisitors.toLocaleString("fr-FR")} sub="visiteurs uniques" testId="kpi-avg" />
              <Kpi icon={Globe} label="Sources" value={sources.length} sub="canaux d'acquisition" testId="kpi-sources" />
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6 mb-8" data-testid="chart-daily">
              <h2 className="text-sm font-semibold text-slate-800 mb-4 uppercase tracking-wider">Trafic quotidien</h2>
              {daily.length === 0 ? (
                <p className="text-sm text-slate-500 py-10 text-center">Aucune donnée sur la période.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={daily} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" stroke="#94a3b8" fontSize={11} />
                    <YAxis stroke="#94a3b8" fontSize={11} />
                    <Tooltip />
                    <Line type="monotone" dataKey="unique_visitors" name="Visiteurs uniques" stroke="#025946" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="pageviews" name="Pages vues" stroke="#C9A25E" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="top-pages">
                <h2 className="text-sm font-semibold text-slate-800 mb-4 uppercase tracking-wider">Top pages</h2>
                {pages.length === 0 ? (
                  <p className="text-sm text-slate-500 py-6 text-center">Aucune donnée.</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {pages.map((p, i) => (
                      <li key={p.path} className="flex items-center justify-between py-3" data-testid={`page-${i}`}>
                        <span className="text-sm text-slate-700 truncate max-w-[60%]">{p.path}</span>
                        <div className="flex items-center gap-4 text-xs">
                          <span className="text-slate-500">{p.unique} uniques</span>
                          <span className="font-semibold text-emerald-700">{p.views} vues</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="sources-card">
                <h2 className="text-sm font-semibold text-slate-800 mb-4 uppercase tracking-wider">Sources de trafic</h2>
                {sources.length === 0 ? (
                  <p className="text-sm text-slate-500 py-6 text-center">Aucune donnée.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={sources.map((s) => ({ ...s, label: SOURCE_LABEL[s.source] || s.source }))} layout="vertical" margin={{ top: 0, right: 20, bottom: 0, left: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" stroke="#94a3b8" fontSize={11} />
                      <YAxis type="category" dataKey="label" stroke="#64748b" fontSize={11} width={90} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#025946" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="devices-card">
              <h2 className="text-sm font-semibold text-slate-800 mb-4 uppercase tracking-wider">Répartition par device</h2>
              <div className="flex items-center gap-6">
                {devices.map((d) => (
                  <div key={d.device} className="flex items-center gap-3" data-testid={`device-${d.device}`}>
                    {d.device === "mobile" ? <Smartphone className="w-5 h-5 text-emerald-700" /> : <Monitor className="w-5 h-5 text-emerald-700" />}
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-500">{d.device}</p>
                      <p className="text-xl font-serif text-slate-900">{d.count.toLocaleString("fr-FR")}</p>
                    </div>
                  </div>
                ))}
                {devices.length === 0 && <p className="text-sm text-slate-500">Aucune donnée.</p>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
